import { StatusDot, Spinner } from "@fox1ck-org/ui";
import type { CheckOfferLinkResponse } from "@gen/tracker/v1/offer_pb";
import { plural, useT, type TKey } from "@/lib/i18n";
import { formatStamp } from "@/lib/utils";
import { useReportTimezone } from "@/lib/services/workspace-service";

const ERROR_KEY: Record<string, TKey> = {
  dns: "u6.link.err.dns",
  timeout: "u6.link.err.timeout",
  tls: "u6.link.err.tls",
  refused: "u6.link.err.refused",
  blocked_address: "u6.link.err.blocked",
  bad_url: "u6.link.err.badUrl",
  http: "u6.link.err.http",
};

/**
 * What «Проверить ссылку» found (boards OfferCard «Куда ведёт», OfferEditor):
 * the first answer, where the link lands, how long it took, and whether the
 * click id rides along — the one thing a partner postback cannot do without.
 */
export function LinkCheckLine({ result, pending }: { result?: CheckOfferLinkResponse; pending?: boolean }) {
  const t = useT();
  const tz = useReportTimezone();
  if (pending) {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner className="size-3" /> {t("u6.link.checking")}
      </span>
    );
  }
  if (!result) return <span className="text-xs text-muted-foreground">{t("u6.link.never")}</span>;
  if (result.error) {
    return (
      <span className="inline-flex items-center gap-2 text-xs">
        <StatusDot tone="danger" />
        {t(ERROR_KEY[result.error] ?? "u6.link.err.http")}
      </span>
    );
  }
  const ok = result.finalStatus > 0 && result.finalStatus < 400 && result.hasClickMacro;
  const hops = result.redirects > 0 ? `, ${result.redirects} ${plural(t("u6.word.redirects"), result.redirects)}` : "";
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      <StatusDot tone={ok ? "ok" : "warn"} />
      <span>
        {t("u6.link.result", {
          at: formatStamp(result.checkedAt, tz),
          status: result.status,
          host: result.finalHost,
          hops,
          ms: Number(result.elapsedMs),
        })}
        {" · "}
        {result.hasClickMacro ? t("u6.link.clickOk") : t("u6.link.clickMissing")}
      </span>
    </span>
  );
}
